import React, { useState, useRef, useEffect } from 'react';
import { Bell, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../Contexts/ProfileContext';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/reducers/userReducer';
import { getNotifications } from '../../services/notificationService';
import NotificationModal from './NotificationModal';

interface HeaderProps {
  title: string;
  showBackButton?: boolean;
  backLink?: any;
}

const Header: React.FC<HeaderProps> = ({ title, showBackButton = false, backLink = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profile = useProfile();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    setShowLogoutModal(false);
    navigate('/login'); // or homepage
  };

  // Close modal on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowLogoutModal(false);
      }
    }

    if (showLogoutModal) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogoutModal]);

  const fetchNotifications = async () => {
    try {
      const response = await getNotifications();

      setUnreadCount(response.unread_count || 0);
      setNotifications(response.notifications?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleNotificationRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id
          ? {
              ...notification,
              read_at: new Date().toISOString(),
            }
          : notification,
      ),
    );

    setUnreadCount((prev) => Math.max(prev - 1, 0));
  };

  const handleAllNotificationsRead = async () => {
    setNotifications((prev) =>
      prev.map((notification) => ({
        ...notification,
        read_at: new Date().toISOString(),
      })),
    );

    setUnreadCount(0);
  };

  return (
    <div className="flex justify-between items-center bg-white px-6 py-4 border-b relative">
      <div className="flex items-center gap-2">
        {showBackButton && (
          <button onClick={() => (backLink ? navigate(backLink) : navigate(-1))}>
            <ArrowLeft size={20} />
          </button>
        )}
        <h1 className="text-sm font-semibold text-[#0c1a3e]">{title}</h1>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative">
          <button onClick={() => setShowNotifications((prev) => !prev)} className="relative">
            <Bell className="text-[#0c1a3e]" size={18} />

            {unreadCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          <NotificationModal
            open={showNotifications}
            notifications={notifications}
            onClose={() => setShowNotifications(false)}
            onNotificationRead={handleNotificationRead}
            onAllNotificationsRead={handleAllNotificationsRead}
          />
        </div>
        {profile && (
          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setShowLogoutModal((prev) => !prev)}
              className="flex items-center gap-2 focus:outline-none"
              aria-haspopup="true"
              aria-expanded={showLogoutModal}
            >
              {/* <img
                src={profile.profile_photo || 'https://i.pravatar.cc/40?img=1'}
                alt="User"
                className="w-8 h-8 rounded-full object-cover border"
              /> */}
              {profile.profile_photo ? (
                <img src={profile.profile_photo} alt="User" className="w-8 h-8 rounded-full object-cover border" />
              ) : (
                <div className="w-8 h-8 rounded-full border bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                  {profile.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
              <div className="leading-tight text-left">
                <p className="text-sm font-semibold text-[#0c1a3e]">{profile.name}</p>
                <p className="text-xs text-gray-500">{profile.user_type?.title}</p>
              </div>
            </button>
            {showLogoutModal && (
              <div ref={modalRef} className="absolute right-0 mt-10 bg-white border shadow-lg rounded px-4 py-2 z-10 w-32">
                <button onClick={handleLogout} className="w-full text-left text-red-600 font-semibold hover:bg-red-50 rounded">
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
